<template>
  <div class="growthbook-features-page">
    <div class="growthbook-features-page_header">
      <div class="growthbook-features-page_header-h1">
        <h1>{{ $t('Menu.GrowthbookFeatures') }}</h1>
        <sChip
          class="my-auto"
          :text="filteredFeatures.length"/>
      </div>
      <sButton
        class="my-auto"
        data-qa="growthbook-refresh"
        icon="repeat"
        :loading="loading"
        small
        type="text"
        @click="refresh">
        {{ $t('GrowthbookFeatures.Refresh') }}
      </sButton>
    </div>
    <sInput
      class="growthbook-features-page_search"
      clearable
      data-qa="growthbook-search"
      :placeholder="$t('Common.Search')"
      :value="searchInput"
      @update:value="onSearchInput"/>
    <sNavTabs
      v-model="tab"
      class="growthbook-features-page_tabs"
      data-qa="growthbook-tab"
      :items="tabs">
      <template #actions>
        <sCheckbox
          v-if="tab === 'config'"
          data-qa="growthbook-problems-only"
          :value="problemsOnly"
          @input="setQuery({ problems: $event ? '1' : undefined, page: undefined })">
          {{ $t('GrowthbookFeatures.ProblemsOnly') }}
        </sCheckbox>
      </template>
    </sNavTabs>

    <template v-if="tab === 'config'">
      <sAlert
        v-if="config.error"
        banner
        data-qa="growthbook-config-error"
        show-icon
        type="error">
        <div data-qa="growthbook-config-error-reason">
          {{ $t(`GrowthbookFeatures.ConfigErrors.${configErrorReason}`) }}
        </div>
        <div
          v-if="config.error.message"
          class="growthbook-features-page_description"
          data-qa="growthbook-config-error-details">
          {{ config.error.message }}
        </div>
      </sAlert>
      <template v-else>
        <div
          class="growthbook-features-page_row-head"
          :style="configColumnsStyle">
          <div>{{ $t('GrowthbookFeatures.Key') }}</div>
          <div>{{ $t('GrowthbookFeatures.Project') }}</div>
          <div>{{ $t('GrowthbookFeatures.ValueType') }}</div>
          <div>{{ $t('GrowthbookFeatures.DefaultValue') }}</div>
          <div
            v-for="environment in config.environments"
            :key="environment.id">
            {{ environment.id }}
          </div>
        </div>
        <div class="growthbook-features-page_grid">
          <EmptyBlock
            v-if="!pageFeatures.length"
            custom-class="mt-0 pa-6 no-members"
            data-qa="features-noData"
            :text="config.loading ? $t('GrowthbookFeatures.Loading') : $t('Tenders.NoData')"/>
          <div
            v-else
            v-for="feature in pageFeatures"
            :key="feature.key"
            class="growthbook-features-page_row is-clickable"
            :class="{ 'is-archived': feature.archived }"
            :data-qa="`growthbook-config-${feature.key}`"
            :style="configColumnsStyle"
            :title="$t('GrowthbookFeatures.Details.Open')"
            @click="detailsFeatureId = feature.key">
            <div>
              <div class="growthbook-features-page_key">
                {{ feature.key }}
              </div>
              <div
                v-if="feature.description"
                class="growthbook-features-page_description">
                {{ feature.description }}
              </div>
              <div
                v-if="feature.archived"
                class="growthbook-features-page_description">
                {{ $t('GrowthbookFeatures.Archived') }}
              </div>
            </div>
            <div>
              <span
                v-if="feature.projectName"
                data-qa="feature-project">
                {{ feature.projectName }}
              </span>
              <span
                v-else
                class="growthbook-features-page_value--false"
                data-qa="feature-no-project">
                {{ $t('GrowthbookFeatures.NoProject') }}
              </span>
            </div>
            <div>{{ feature.valueType }}</div>
            <div>
              <span :class="valueClass(feature.defaultValue)">{{ feature.defaultValue }}</span>
            </div>
            <div
              v-for="environment in feature.environments"
              :key="environment.id"
              :data-qa="`feature-env-${environment.id}`">
              <sIcon
                :color="environment.enabled ? 'var(--alert-success-icon)' : 'var(--alert-error-icon)'"
                :type="environment.enabled ? 'accept' : 'cancel'"/>
              <span
                v-if="environment.rulesCount"
                class="growthbook-features-page_description ml-1">
                {{ $t('GrowthbookFeatures.RulesCount', { count: environment.rulesCount }) }}
              </span>
            </div>
          </div>
        </div>
      </template>
    </template>

    <template v-else>
      <div class="growthbook-features-page_row-head">
        <div>{{ $t('GrowthbookFeatures.Key') }}</div>
        <div>{{ $t('GrowthbookFeatures.Value') }}</div>
      </div>
      <div class="growthbook-features-page_grid">
        <EmptyBlock
          v-if="!pageFeatures.length"
          custom-class="mt-0 pa-6 no-members"
          data-qa="features-noData"
          :text="$t('Tenders.NoData')"/>
        <div
          v-else
          v-for="feature in pageFeatures"
          :key="feature.key"
          class="growthbook-features-page_row"
          :data-qa="`growthbook-feature-${feature.key}`">
          <div class="growthbook-features-page_key">
            {{ feature.key }}
          </div>
          <div>
            <span :class="valueClass(feature.value)">{{ formatValue(feature.value) }}</span>
          </div>
        </div>
      </div>
    </template>

    <div
      v-if="pagesCount > 1"
      class="growthbook-features-page_pagination">
      <sButton
        v-for="pageNumber in pagesCount"
        :key="pageNumber"
        :data-qa="`growthbook-page-${pageNumber}`"
        :type="pageNumber === currentPage ? 'black' : 'text'"
        @click="setQuery({ page: pageNumber })">
        {{ pageNumber }}
      </sButton>
    </div>

    <GrowthbookFeatureDetailsModal
      :environments="config.environments"
      :feature="detailsFeature"
      :projects="config.projects"
      :value="!!detailsFeature"
      @close="detailsFeatureId = null"/>
  </div>
</template>

<script>
import EmptyBlock from 'components/common/elements/EmptyBlock.vue'
import GrowthbookFeatureDetailsModal from 'components/growthbook/GrowthbookFeatureDetailsModal.vue'

import { mapGetters } from 'vuex'
import SettingsApi from '@/api/settingsApi'
import godModeMixin from 'mixins/godModeMixin'
import growthbookFeaturesAccessMixin from 'mixins/growthbookFeaturesAccessMixin'
import { debounce } from 'toolkit/utils/util'

const settingsApi = new SettingsApi()
const PAGE_SIZE = 20
const TABS = ['values', 'config']

export default {
  name: 'GrowthbookFeatures',
  mixins: [
    godModeMixin,
    growthbookFeaturesAccessMixin,
  ],
  data() {
    return {
      loading: false,
      searchInput: '',
      // id фічі, відкритої в модалці перегляду
      detailsFeatureId: null,
      config: {
        loading: false,
        loaded: false,
        // null або { message } — текст помилки від бека/мережі
        error: null,
        features: [],
        environments: [],
        projects: [],
      },
    }
  },
  methods: {
    // Оновлює лише дані активної вкладки. Серверний кеш не скидаємо (лише читання) — gateway сам
    // оновлює його за TTL (GrowthbookFeatures:CacheDurationMinutes: 15 хв, на тестовому оточенні — 5)
    async refresh() {
      this.loading = true
      try {
        if (this.tab === 'config') {
          // Проєкти й оточення бек кешує на добу — перезапитуємо лише фічі, якщо конфігурація вже є
          await (this.config.loaded ? this.reloadConfigFeatures() : this.loadConfig())
        } else {
          this.$store.commit('setGrowthBook', [])
          await this.$store.dispatch('setGrowthBook')
        }
      } finally {
        this.loading = false
      }
    },
    async reloadConfigFeatures() {
      this.config.loading = true
      try {
        const features = await settingsApi.growthbookAdminFeatures()
        if (Array.isArray(features)) {
          this.config.features = features
          this.config.error = null
        } else {
          this.config.error = { message: features?.error || '' }
          this.config.loaded = false
        }
      } finally {
        this.config.loading = false
      }
    },
    async loadConfig() {
      this.config.loading = true
      try {
        const [features, environments, projects] = await Promise.all([
          settingsApi.growthbookAdminFeatures(),
          settingsApi.growthbookAdminEnvironments(),
          settingsApi.growthbookAdminProjects(),
        ])
        // Помилка — { error: 'текст' }; показуємо першу, решта зазвичай з тієї ж причини
        // findIndex, а не find: невдалий результат може бути null/undefined
        const results = [features, environments, projects]
        const failedIndex = results.findIndex(result => !Array.isArray(result))
        this.config.error = failedIndex === -1 ? null : { message: results[failedIndex]?.error || '' }
        this.config.features = Array.isArray(features) ? features : []
        this.config.environments = Array.isArray(environments) ? environments : []
        this.config.projects = Array.isArray(projects) ? projects : []
        this.config.loaded = failedIndex === -1
      } finally {
        this.config.loading = false
      }
    },
    onSearchInput(value) {
      this.searchInput = value || ''
      this.applySearch()
    },
    applySearch: debounce(function() {
      this.setQuery({ search: this.searchInput.trim() || undefined, page: undefined })
    }, 300),
    setQuery(params) {
      const query = { ...this.$route.query, ...params }
      Object.keys(query).forEach(key => {
        if (query[key] === undefined || query[key] === 1) {
          delete query[key]
        }
      })
      this.$router.replace({ query }).catch(() => {})
    },
    formatValue(value) {
      if (value !== null && typeof value === 'object') {
        return JSON.stringify(value)
      }
      return String(value)
    },
    valueClass(value) {
      const text = String(value)
      if (text === 'true') {
        return 'growthbook-features-page_value--true'
      }
      if (text === 'false') {
        return 'growthbook-features-page_value--false'
      }
      return ''
    },
  },
  computed: {
    ...mapGetters({
      growthBook: 'getGrowthBook',
    }),
    tab: {
      get() {
        return TABS.includes(this.$route.query.tab) ? this.$route.query.tab : 'values'
      },
      set(value) {
        this.setQuery({ tab: value === 'values' ? undefined : value, page: undefined, problems: undefined })
      },
    },
    tabs() {
      return [
        { value: 'values', label: this.$t('GrowthbookFeatures.TabValues'), count: this.valueFeatures.length },
        {
          value: 'config',
          label: this.$t('GrowthbookFeatures.TabConfig'),
          count: this.config.loaded ? this.configFeatures.length : null,
          loading: this.config.loading,
        },
      ]
    },
    search() {
      return (this.$route.query.search || '').toString().trim().toLowerCase()
    },
    problemsOnly() {
      return this.$route.query.problems === '1'
    },
    valueFeatures() {
      return Object.keys(this.growthBook || {})
        .sort((a, b) => a.localeCompare(b))
        .map(key => ({ key, value: this.growthBook[key] }))
    },
    detailsFeature() {
      return this.config.features.find(feature => feature.id === this.detailsFeatureId) || null
    },
    configFeatures() {
      const projectNames = Object.fromEntries(this.config.projects.map(project => [project.id, project.name || project.id]))
      return this.config.features
        .map(feature => {
          const environments = this.config.environments.map(({ id }) => {
            const environment = (feature.environments || []).find(item => item.id === id)
            return {
              id,
              enabled: !!environment?.enabled,
              rulesCount: environment?.rules?.length || 0,
            }
          })
          return {
            key: feature.id,
            description: feature.description,
            archived: feature.archived,
            projectName: feature.project ? projectNames[feature.project] || feature.project : '',
            valueType: feature.valueType,
            defaultValue: feature.defaultValue,
            environments,
            // Типові причини, чому фіча «не приходить»: не вказано Project або вимкнено в оточенні
            hasProblems: !feature.archived && (!feature.project || environments.some(environment => !environment.enabled)),
          }
        })
        .sort((a, b) => a.key.localeCompare(b.key))
    },
    features() {
      return this.tab === 'config' ? this.configFeatures : this.valueFeatures
    },
    filteredFeatures() {
      return this.features.filter(feature =>
        (!this.search || feature.key.toLowerCase().includes(this.search))
        && (this.tab !== 'config' || !this.problemsOnly || feature.hasProblems))
    },
    // Причина помилки завантаження конфігурації — за текстом від бека (GrowthbookApiClient /
    // політика AdminAuthorized) або мережі. Ключ — у GrowthbookFeatures.ConfigErrors
    configErrorReason() {
      const message = this.config.error?.message || ''
      if (/ApiKey.*not configured/i.test(message)) {
        return 'NoApiKey'
      }
      if (/returned (401|403)\b/.test(message)) {
        return 'InvalidApiKey'
      }
      if (/returned 429\b/.test(message)) {
        return 'RateLimit'
      }
      if (/not authorized|access denied|AdminAuthorized/i.test(message)) {
        return 'AccessDenied'
      }
      if (/network error|failed to fetch/i.test(message)) {
        return 'Network'
      }
      return 'Unknown'
    },
    configColumnsStyle() {
      return { gridTemplateColumns: `1fr 150px 90px 120px repeat(${this.config.environments.length}, 120px)` }
    },
    pagesCount() {
      return Math.ceil(this.filteredFeatures.length / PAGE_SIZE)
    },
    currentPage() {
      const page = parseInt(this.$route.query.page, 10) || 1
      return Math.min(Math.max(page, 1), Math.max(this.pagesCount, 1))
    },
    pageFeatures() {
      const start = (this.currentPage - 1) * PAGE_SIZE
      return this.filteredFeatures.slice(start, start + PAGE_SIZE)
    },
  },
  watch: {
    '$route.query.search'(value) {
      if ((value || '') !== this.searchInput.trim()) {
        this.searchInput = value || ''
      }
    },
    tab(value) {
      if (value === 'config' && !this.config.loaded && !this.config.loading) {
        this.loadConfig()
      }
    },
  },
  created() {
    if (!this.mixin_isGodMode || !this.growthBook?.growthbookFeaturesPage || !this.$_hasGrowthbookFeaturesRole) {
      this.$router.push({ name: 'page-error', params: { status_code: '403' } })
      return
    }
    this.searchInput = (this.$route.query.search || '').toString()
    if (this.tab === 'config') {
      this.loadConfig()
    }
  },
  components: {
    EmptyBlock,
    GrowthbookFeatureDetailsModal,
  }
}
</script>

<style scoped lang="scss">
.growthbook-features-page {
  max-width: 1280px;
  margin: 0 auto;
  &_header {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: 10px;
    &-h1 {
      display: flex;
      gap: 10px;
    }
  }
  &_search {
    margin-bottom: 10px;
  }
  &_tabs {
    margin-top: 10px;
    margin-bottom: 10px;
  }
  &_grid {
    display: grid;
    gap: 1px;
  }
  &_grid>*:last-child {
    border-radius: 0 0 10px 10px;
  }
  &_row {
    display: grid;
    gap: 1px;
    grid-template-columns: 1fr 100px;
    text-align: left;
    color: var(--text-color) !important;
    font-size: 14px;
    line-height: 18px;
    background-color: var(--v-main__green-base) !important;
    border-right: 1px solid var(--v-main__green-base) !important;
    &>div {
      padding: 10px;
      word-break: break-word;
      background-color: var(--v-main__light_green-base) !important;
    }
    &.is-clickable {
      cursor: pointer;
      &:hover>div {
        background-color: var(--v-secondary__green_chosen-base) !important;
      }
    }
    &.is-archived>div {
      opacity: 0.6;
    }
    &-head {
      display: grid;
      grid-template-columns: 1fr 100px;
      text-align: left;
      border-radius: 10px 10px 0 0;
      background-color: var(--v-secondary__green_chosen-base) !important;
      color: var(--v-secondary__green_not_active-base);
      font-weight: 500;
      height: 40px;
      font-size: 11px !important;
      text-transform: uppercase;
      &>* {
        border-right: 1px solid var(--v-main__green-base) !important;
        border-bottom: 1px solid var(--v-main__green-base) !important;
        padding: 10px;
      }
    }
  }
  &_key {
    font-weight: 500;
  }
  &_description {
    font-size: 12px;
    color: var(--v-secondary__green_not_active-base);
  }
  &_value {
    &--true {
      color: var(--alert-success-icon);
      font-weight: 500;
    }
    &--false {
      color: var(--text-red, #e53935);
      font-weight: 500;
    }
  }
  &_pagination {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 5px;
    margin-top: 10px;
  }
}
</style>
